import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import LWCPreview from '../components/LWCPreview';
import { useAuth } from '../context/AuthContext';
import { useTheme, TOKENS } from '../context/ThemeContext';
import { useUserTier } from '../context/UserTierContext';
import BACKEND_URL from '../config';
import SEO from '../components/SEO';
import { useAuthModal } from '../context/AuthModalContext';
import EinsteinMascot from '../components/EinsteinMascot';
import DescriptionSection from '../components/DescriptionSection';

// Apex30 challenges belong on /apex30, not /challenges/apex30
const backUrl = (category) => category === 'apex30' ? '/apex30' : `/challenges/${category}`;

const topicIcons = {
  'Apex Fundamentals':      '📘',
  'Data Querying':          '🔍',
  'Data Manipulation':      '💾',
  'Object-Oriented Apex':   '🏗️',
  'Apex Triggers':          '⚡',
  'Asynchronous Apex':      '⏳',
  'Apex Testing':           '🧪',
  'Error Handling':         '🚨',
  'Custom Metadata':        '📋',
  'Working with JSON':      '🔄',
  'LWC Fundamentals':       '🖥️',
  'LWC - Apex Integration': '🖥️',
};

function formatTime(s) {
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

function Confetti({ show }) {
  if (!show) return null;
  const colors = ['#4f8ef7', '#60a5fa', '#e8eaf0', '#a5b4fc'];
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 9999, overflow: 'hidden' }}>
      {Array.from({ length: 28 }).map((_, i) => (
        <div key={i} style={{ position: 'absolute', top: '-20px', left: `${Math.random() * 100}%`, width: `${5 + Math.random() * 7}px`, height: `${5 + Math.random() * 7}px`, background: colors[i % colors.length], borderRadius: i % 3 === 0 ? '50%' : '2px', animation: `fall ${1.2 + Math.random() * 0.8}s ${Math.random() * 0.5}s ease-in forwards` }} />
      ))}
      <style>{`@keyframes fall{0%{transform:translateY(0) rotate(0);opacity:1}100%{transform:translateY(100vh) rotate(720deg);opacity:0}}`}</style>
    </div>
  );
}

// ── Paywall overlay ───────────────────────────────────────────────────────────
function PaywallOverlay({ challenge, c, theme, navigate, openAuthModal, user, tier }) {
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: theme === 'dark' ? 'rgba(8,9,14,0.92)' : 'rgba(255,255,255,0.92)', backdropFilter: 'blur(6px)' }}>
      <div style={{ maxWidth: 400, width: '100%', margin: '0 24px', background: c.card, border: `1px solid ${c.border}`, borderRadius: 16, padding: '36px 32px', textAlign: 'center', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: c.text, margin: '0 0 8px' }}>
          {challenge.difficulty === 'Hard' ? 'Pro Challenge' : 'Upgrade to Continue'}
        </h2>
        <p style={{ fontSize: 13, color: c.secondary, lineHeight: 1.7, margin: '0 0 24px' }}>
          {challenge.difficulty === 'Hard'
            ? 'Hard challenges are available on the Pro plan. Upgrade to unlock all challenges, solutions and more.'
            : `You've used your free ${challenge.difficulty} challenge for this topic. Upgrade to Pro to unlock all challenges.`
          }
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button onClick={() => navigate('/pricing')} style={{ background: c.accent, color: theme === 'dark' ? c.bg : '#fff', border: 'none', padding: '12px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
            ⚡ Upgrade to Pro — $9/mo
          </button>
          {!user && (
            <button onClick={() => openAuthModal()} style={{ background: 'transparent', border: `1px solid ${c.border}`, color: c.secondary, padding: '10px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
              Sign in first
            </button>
          )}
          <button onClick={() => navigate(backUrl(challenge.category))} style={{ background: 'transparent', border: 'none', color: c.muted, cursor: 'pointer', fontSize: 12 }}>
            ← Back to challenges
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Challenge() {
  const { id }            = useParams();
  const navigate          = useNavigate();
  const { user }          = useAuth();
  const { theme, toggle } = useTheme();
  const c                 = TOKENS[theme];
  const { openAuthModal } = useAuthModal();
  const { tier, canAccessChallenge, canViewSolution } = useUserTier();

  const diffStyle = (diff) => ({
    Easy:   { color: c.success, bg: theme === 'light' ? '#d1fae5' : '#051a10' },
    Medium: { color: c.warning, bg: theme === 'light' ? '#fef3c7' : '#1a1000' },
    Hard:   { color: c.error,   bg: theme === 'light' ? '#fee2e2' : '#1a0808' },
  }[diff] || { color: c.success, bg: theme === 'light' ? '#d1fae5' : '#051a10' });

  const successBg     = theme === 'light' ? '#d1fae5' : '#051a10';
  const successBorder = theme === 'light' ? '#6ee7b7' : '#065f46';
  const errorBg       = theme === 'light' ? '#fee2e2' : '#1a0808';
  const editorBg      = theme === 'light' ? '#f8f9fd' : '#070810';

  const [challenge, setChallenge]         = useState(null);
  const [loading, setLoading]             = useState(true);
  const [code, setCode]                   = useState('');
  const [results, setResults]             = useState(null);
  const [validating, setValidating]       = useState(false);
  const [allChallenges, setAllChallenges] = useState([]);
  const [currentIndex, setCurrentIndex]   = useState(-1);
  const [showOrgType, setShowOrgType]     = useState(false);
  const [showConfetti, setShowConfetti]   = useState(false);
  const [isLocked, setIsLocked]           = useState(false);
  // Track which topics the user has already used their free Medium on
  const [usedMediumTopics, setUsedMediumTopics] = useState(new Set());

  const [elapsed, setElapsed] = useState(0);
  const timerRef              = useRef(null);
  const startTimeRef          = useRef(null);

  useEffect(() => {
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000)), 1000);
    return () => clearInterval(timerRef.current);
  }, [id]);

  useEffect(() => {
    setLoading(true); setResults(null); setCode('');
    fetch(`${BACKEND_URL}/challenges/${id}`)
      .then(r => r.json())
      .then(data => {
        setChallenge(data);
        if (data.is_lwc) {
          setCode({ js: data.lwc_starter_js || '', html: data.lwc_starter_html || '' });
        } else {
          setCode(data.starter_code || '');
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  // Fetch user's solved Medium topics to determine free slot usage
  useEffect(() => {
    if (!user) return;
    fetch(`${BACKEND_URL}/submissions/${user.id}`)
      .then(r => r.json())
      .then(data => {
        if (data.solvedIds && allChallenges.length > 0) {
          const solvedSet = new Set(data.solvedIds.map(String));
          const usedTopics = new Set();
          allChallenges.forEach(ch => {
            if (ch.difficulty === 'Medium' && solvedSet.has(String(ch.id))) {
              usedTopics.add(ch.topic);
            }
          });
          setUsedMediumTopics(usedTopics);
        }
      })
      .catch(() => {});
  }, [user, allChallenges]);

  // Check paywall after challenge + tier are known
  useEffect(() => {
    if (!challenge || loading) return;
    const locked = !canAccessChallenge(challenge, usedMediumTopics);
    setIsLocked(locked);
  }, [challenge, tier, usedMediumTopics, loading, canAccessChallenge]);

  useEffect(() => {
    if (!challenge) return;
    fetch(`${BACKEND_URL}/challenges`)
      .then(r => r.json())
      .then(data => {
        const cat = data.filter(ch => ch.category === challenge.category);
        setAllChallenges(cat);
        setCurrentIndex(cat.findIndex(ch => ch.id === parseInt(id)));
      });
  }, [challenge, id]);

  const handleValidate = async () => {
    if (challenge.is_lwc) return;
    const token    = localStorage.getItem('sf_token');
    const instance = localStorage.getItem('sf_instance');
    if (!token || !instance) {
      setResults({ success: false, errors: ['Connect your Salesforce org first'] });
      return;
    }
    setValidating(true); setResults(null);
    clearInterval(timerRef.current);
    const timeSpent = elapsed;
    try {
      const response = await fetch(`${BACKEND_URL}/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, instance, code: code || challenge.starter_code, challengeId: parseInt(id), userId: user?.id || null, timeSpentSeconds: timeSpent }),
      });
      const data = await response.json();
      if (response.status === 401 || data.tokenExpired) {
        localStorage.removeItem('sf_token');
        localStorage.removeItem('sf_instance');
        setResults({ success: false, tokenExpired: true });
        setValidating(false);
        return;
      }
      if (data.success) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 2500);
        if (user?.id) {
          try {
            await fetch(`${BACKEND_URL}/update-stats`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ userId: user.id, challengeId: parseInt(id), difficulty: challenge.difficulty, isSuccess: true }),
            });
          } catch (e) { console.error('Stats update failed:', e); }
        }
      } else {
        startTimeRef.current = Date.now();
        timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000)), 1000);
      }
      setResults(data);
    } catch (err) {
      setResults({ success: false, errors: [err.message] });
    } finally {
      setValidating(false);
    }
  };

  const handleReset = () => {
    setResults(null);
    if (challenge.is_lwc) {
      setCode({ js: challenge.lwc_starter_js || '', html: challenge.lwc_starter_html || '' });
    } else {
      setCode(challenge.starter_code || '');
    }
    setElapsed(0);
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000)), 1000);
  };

  const goToChallenge = (index) => { setResults(null); setCode(''); navigate(`/challenge/${allChallenges[index].id}`); };

  if (loading) return (
    <div style={{ background: c.bg, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: c.secondary, fontSize: '14px' }}>Loading challenge...</p>
    </div>
  );
  if (!challenge) return (
    <div style={{ background: c.bg, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px' }}>
      <p style={{ color: c.text }}>Challenge not found</p>
      <button onClick={() => navigate('/dashboard')} style={{ background: c.accent, color: theme === 'dark' ? c.bg : '#fff', border: 'none', padding: '10px 20px', borderRadius: '7px', cursor: 'pointer', fontWeight: 700 }}>Go Home</button>
    </div>
  );

  const ds    = diffStyle(challenge.difficulty);
  const isLWC = challenge.is_lwc || challenge.category === 'lwc';
  const isLWCIntegration = challenge.is_lwc_integration === true;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: c.bg, fontFamily: "'Inter', -apple-system, sans-serif", transition: 'background 0.25s, color 0.25s', position: 'relative' }}>

      <SEO
        title={challenge.title}
        description={challenge.description || `Solve the ${challenge.title} Salesforce coding challenge on TrailCode.`}
        url={`https://trailcode.org/challenge/${challenge.id}`}
      />

      <Confetti show={showConfetti} />

      {/* Paywall overlay */}
      {isLocked && (
        <PaywallOverlay
          challenge={challenge}
          c={c} theme={theme}
          navigate={navigate}
          openAuthModal={openAuthModal}
          user={user}
          tier={tier}
        />
      )}

      {/* Breadcrumb nav — keep custom for editor context */}
      <nav style={{ flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 24px', height: '52px', background: c.navBg, backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: `1px solid ${c.border}`, transition: 'background 0.25s, border-color 0.25s' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
          {['TrailCode', challenge.category === 'apex30' ? 'Apex30' : challenge.category?.toUpperCase(), challenge.title].map((crumb, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span style={{ color: c.muted }}>›</span>}
              <span style={{ color: i === 2 ? c.secondary : c.muted, cursor: i < 2 ? 'pointer' : 'default', transition: 'color 0.15s' }}
                onClick={() => { if (i === 0) navigate('/dashboard'); if (i === 1) navigate(backUrl(challenge.category)); }}
                onMouseEnter={e => { if (i < 2) e.target.style.color = c.text; }}
                onMouseLeave={e => { if (i < 2) e.target.style.color = c.muted; }}>
                {crumb}
              </span>
            </React.Fragment>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px' }}>
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '6px', padding: '4px 12px', display: 'flex', alignItems: 'center', gap: '6px', color: elapsed > 1800 ? c.warning : c.secondary, fontFamily: 'monospace', fontSize: '13px' }}>⏱ {formatTime(elapsed)}</div>
          <span style={{ color: ds.color, background: ds.bg, padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>{challenge.difficulty}</span>
          <span style={{ color: c.secondary }}>·</span>
          <span style={{ color: c.secondary }}>{topicIcons[challenge.topic]} {challenge.topic}</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Theme toggle */}
          <button onClick={toggle} aria-label="Toggle theme"
            style={{ width: 46, height: 26, background: c.card, border: `1px solid ${c.border}`, borderRadius: 13, cursor: 'pointer', position: 'relative', flexShrink: 0 }}>
            <div style={{ position: 'absolute', top: 3, left: theme === 'light' ? 21 : 3, width: 18, height: 18, background: theme === 'light' ? '#f59e0b' : c.accent, borderRadius: '50%', transition: 'left 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>
              {theme === 'light' ? '☀️' : '🌙'}
            </div>
          </button>
          {user ? (
            <>
              <span style={{ color: c.secondary, cursor: 'pointer', fontSize: '13px', transition: 'color 0.15s' }}
                onClick={() => navigate('/profile')}
                onMouseEnter={e => e.target.style.color = c.text}
                onMouseLeave={e => e.target.style.color = c.secondary}>Progress</span>
              {user.user_metadata?.avatar_url && <img src={user.user_metadata.avatar_url} alt="avatar" style={{ width: '28px', height: '28px', borderRadius: '50%', border: `1.5px solid ${c.border}` }} />}
            </>
          ) : (
            <button onClick={() => openAuthModal()} style={{ background: c.accent, color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>Login</button>
          )}
        </div>
      </nav>

      {/* 3 panels */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Panel 1 — Problem */}
        <div style={{ width: '35%', borderRight: `1px solid ${c.border}`, overflowY: 'auto', padding: '20px', background: c.bg, transition: 'background 0.25s, border-color 0.25s' }}>
          <h1 style={{ fontSize: '17px', fontWeight: 700, marginBottom: '18px', color: c.text, lineHeight: 1.4 }}>{challenge.title}</h1>
              {/* Problem / Requirements / Concept — unified tabbed panel */}
            <DescriptionSection challenge={challenge} c={c} theme={theme} />
        </div>

        {/* Panel 2 — Editor */}
        {isLWC ? (
          <>
            <div style={{ width: '40%', borderRight: `1px solid ${c.border}`, display: 'flex', flexDirection: 'column', background: editorBg }}>
              <div style={{ display: 'flex', borderBottom: `1px solid ${c.border}`, flexShrink: 0, background: c.bgAlt }}>
                {[
                  { key: 'js',   label: `${challenge.lwc_component_name || 'component'}.js`,   lang: 'JS'   },
                  { key: 'html', label: `${challenge.lwc_component_name || 'component'}.html`, lang: 'HTML' },
                ].map(tab => {
                  const isActive = (typeof code === 'object' ? (code._tab || 'js') : 'js') === tab.key;
                  return (
                    <button key={tab.key}
                      onClick={() => setCode(prev => typeof prev === 'object' ? { ...prev, _tab: tab.key } : { js: challenge.lwc_starter_js || '', html: challenge.lwc_starter_html || '', _tab: tab.key })}
                      style={{ padding: '9px 16px', border: 'none', borderBottom: isActive ? `2px solid ${c.accent}` : '2px solid transparent', background: isActive ? editorBg : 'none', color: isActive ? c.text : c.secondary, fontSize: 12, fontWeight: isActive ? 600 : 400, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 9, fontWeight: 700, color: tab.key === 'js' ? '#f59e0b' : '#60a5fa', background: tab.key === 'js' ? 'rgba(245,158,11,0.12)' : 'rgba(96,165,250,0.12)', padding: '1px 5px', borderRadius: 3 }}>{tab.lang}</span>
                      {tab.label}
                    </button>
                  );
                })}
              </div>
              <Editor
                height="100%"
                language={(typeof code === 'object' ? (code._tab || 'js') : 'js') === 'js' ? 'javascript' : 'html'}
                theme={theme === 'dark' ? 'vs-dark' : 'light'}
                value={typeof code === 'object' ? (code[(code._tab || 'js')] || '') : ''}
                onChange={val => setCode(prev => typeof prev === 'object' ? { ...prev, [prev._tab || 'js']: val } : prev)}
                options={{ fontSize: 13, minimap: { enabled: false }, scrollBeyondLastLine: false, automaticLayout: true, padding: { top: 14 }, fontFamily: "'JetBrains Mono', 'Fira Code', monospace" }}
              />
            </div>
            <LWCPreview
              componentName={challenge.lwc_component_name}
              jsCode={typeof code === 'object' ? code.js : ''}
              htmlCode={typeof code === 'object' ? code.html : ''}
              mockData={challenge.lwc_mock_data}
              domChecks={challenge.lwc_dom_checks}
              isLWCIntegration={isLWCIntegration}
              sfToken={localStorage.getItem('sf_token')}
              sfInstance={localStorage.getItem('sf_instance')}
              challenge={challenge}
              userId={user?.id}
              c={c} theme={theme}
              setShowOrgType={setShowOrgType}
              showOrgType={showOrgType}
              BACKEND_URL={BACKEND_URL}
              onSuccess={() => { setShowConfetti(true); setTimeout(() => setShowConfetti(false), 2500); }}
            />
          </>
        ) : (
          <div style={{ flex: 1, borderRight: `1px solid ${c.border}`, display: 'flex', flexDirection: 'column', background: editorBg }}>
            <div style={{ padding: '8px 16px', borderBottom: `1px solid ${c.border}`, background: c.bgAlt, display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '2px 6px', borderRadius: '3px' }}>APEX</span>
              <span style={{ fontSize: '12px', color: c.muted, fontFamily: 'monospace' }}>{challenge.class_name}.cls</span>
            </div>
            <Editor
              height="100%"
              defaultLanguage="apex"
              theme={theme === 'dark' ? 'vs-dark' : 'light'}
              value={typeof code === 'string' ? code : ''}
              onChange={val => setCode(val || '')}
              options={{ fontSize: 13, minimap: { enabled: false }, scrollBeyondLastLine: false, automaticLayout: true, padding: { top: 14 }, lineNumbers: 'on', fontFamily: "'JetBrains Mono', 'Fira Code', monospace" }}
            />
          </div>
        )}

        {/* Panel 3 — Results (Apex only) */}
        {!isLWC && (
          <div style={{ width: '25%', overflowY: 'auto', padding: '16px', background: c.bg, display: 'flex', flexDirection: 'column', gap: '10px', transition: 'background 0.25s' }}>

            {results?.success && (
              <div style={{ background: successBg, border: `1px solid ${successBorder}`, borderRadius: '10px', padding: '18px', textAlign: 'center' }}>
                <div style={{ fontSize: '36px', marginBottom: '8px' }}>☁️</div>
                <p style={{ color: c.success, fontWeight: 700, fontSize: '15px', marginBottom: '4px' }}>You did it!</p>
                <p style={{ color: c.success, fontSize: '12px', opacity: 0.8, marginBottom: '8px' }}>{results.passed}/{results.total} tests passed</p>
                <p style={{ color: c.secondary, fontSize: '11px', marginBottom: '14px' }}>⏱ Solved in {formatTime(elapsed)}</p>
                <button onClick={handleReset} style={{ background: 'transparent', border: `1px solid ${c.success}`, color: c.success, padding: '8px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', width: '100%' }}
                  onMouseEnter={e => { e.target.style.background = c.success; e.target.style.color = theme === 'dark' ? c.bg : '#fff'; }}
                  onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.color = c.success; }}>
                  🔄 Reset & Practice Again
                </button>
                {/* View solution — Pro only */}
                {canViewSolution() ? (
                  challenge.solution && (
                    <button onClick={() => {/* TODO: show solution modal */}} style={{ background: 'transparent', border: `1px solid ${c.border}`, color: c.secondary, padding: '8px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', width: '100%', marginTop: 8 }}>
                      📖 View Solution
                    </button>
                  )
                ) : (
                  <button onClick={() => navigate('/pricing')} style={{ background: 'transparent', border: `1px solid ${c.border}`, color: c.muted, padding: '8px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', width: '100%', marginTop: 8 }}>
                    🔒 View Solution — Pro only
                  </button>
                )}
              </div>
            )}

            {!results?.success && !isLWC && (
              <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '8px', padding: '14px', transition: 'background 0.25s, border-color 0.25s' }}>
                <p style={{ fontSize: '10px', fontWeight: 600, color: c.accent, textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 10px' }}>Salesforce Org</p>
                {localStorage.getItem('sf_token') ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                      <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: c.success, display: 'inline-block' }} />
                      <span style={{ fontSize: '12px', color: c.success, fontWeight: 500 }}>Connected</span>
                    </div>
                    <button onClick={() => { localStorage.removeItem('sf_token'); localStorage.removeItem('sf_instance'); window.location.reload(); }}
                      style={{ background: 'transparent', border: `1px solid ${c.border}`, color: c.secondary, width: '100%', padding: '6px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}>Disconnect</button>
                  </div>
                ) : !showOrgType ? (
                  <button onClick={() => setShowOrgType(true)} style={{ background: c.accentBg, border: `1px solid ${c.accent}`, color: c.accent, width: '100%', padding: '9px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>⚡ Connect Org</button>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <p style={{ fontSize: '11px', color: c.secondary, margin: 0 }}>Select org type:</p>
                    <button onClick={() => window.location.href = `${BACKEND_URL}/auth/salesforce?type=login`}
                      style={{ background: c.accentBg, border: `1px solid ${c.accent}`, color: c.accent, padding: '8px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Production / Dev Org</button>
                    <button onClick={() => window.location.href = `${BACKEND_URL}/auth/salesforce?type=test`}
                      style={{ background: 'transparent', border: `1px solid ${c.border}`, color: c.secondary, padding: '8px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Sandbox</button>
                    <button onClick={() => setShowOrgType(false)} style={{ background: 'transparent', border: 'none', color: c.muted, cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                  </div>
                )}
              </div>
            )}

            {!results?.success && !isLWC && (
              !user ? (
                <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '8px', padding: '16px', textAlign: 'center', transition: 'background 0.25s, border-color 0.25s' }}>
                  <p style={{ color: c.secondary, fontSize: '12px', marginBottom: '12px', lineHeight: 1.6 }}>Sign in to validate and track progress</p>
                  <button onClick={() => openAuthModal()} style={{ background: '#ffffff', color: '#111', border: 'none', padding: '10px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <img src="https://www.google.com/favicon.ico" alt="G" style={{ width: '14px', height: '14px' }} />
                    Sign in with Google
                  </button>
                </div>
              ) : (
                <button onClick={handleValidate} disabled={validating} style={{ background: validating ? c.card : c.accent, color: validating ? c.secondary : (theme === 'dark' ? c.bg : '#fff'), border: 'none', padding: '13px', borderRadius: '7px', cursor: validating ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 700, width: '100%', transition: 'all 0.15s' }}>
                  {validating ? '⏳ Running tests...' : '🚀 Validate Solution'}
                </button>
              )
            )}

            {results && !results.success && !isLWC && (
              <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '8px', padding: '14px', transition: 'background 0.25s, border-color 0.25s' }}>
                {results.tokenExpired ? (
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: '14px', marginBottom: '8px', color: c.text }}>🔑 Session expired</p>
                    <p style={{ fontSize: '12px', color: c.secondary, marginBottom: '14px' }}>Reconnect your org to continue.</p>
                    <button onClick={() => setShowOrgType(true)} style={{ background: c.accentBg, border: `1px solid ${c.accent}`, color: c.accent, width: '100%', padding: '9px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>⚡ Reconnect</button>
                  </div>
                ) : results.errors && (!results.testResults || results.testResults.length === 0) ? (
                  <div>
                    <p style={{ fontSize: '12px', fontWeight: 700, color: c.error, margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: '6px' }}>🔴 {results.compileError ? 'Compilation Error' : 'No Tests Ran'}</p>
                    {results.errors.map((err, i) => (
                      <div key={i} style={{ background: errorBg, border: i === 0 ? `0.5px solid ${theme === 'light' ? '#fca5a5' : '#3a1111'}` : 'none', borderRadius: i === 0 ? '6px' : '0', padding: i === 0 ? '10px' : '2px 10px', marginBottom: i === 0 ? '6px' : 0 }}>
                        <p style={{ fontSize: '12px', color: i === 0 ? c.error : c.secondary, margin: 0, lineHeight: 1.6, fontFamily: i === 0 ? 'monospace' : 'inherit' }}>{err}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: c.text, marginBottom: '6px' }}>❌ {results.failed} test{results.failed !== 1 ? 's' : ''} failed</p>
                    <p style={{ fontSize: '12px', color: c.secondary, marginBottom: '12px' }}>{results.passed}/{results.total} passed</p>
                    {results.testResults?.map((test, i) => (
                      <div key={i} style={{ background: test.outcome === 'Pass' ? successBg : errorBg, border: `0.5px solid ${test.outcome === 'Pass' ? successBorder : (theme === 'light' ? '#fca5a5' : '#3a1111')}`, borderRadius: '6px', padding: '9px', marginBottom: '6px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '11px', fontWeight: 600, color: c.text }}>{test.outcome === 'Pass' ? '✅' : '❌'} {test.methodName}</span>
                          <span style={{ fontSize: '10px', color: test.outcome === 'Pass' ? c.success : c.error }}>{test.outcome}</span>
                        </div>
                        {test.message && <p style={{ fontSize: '11px', color: c.secondary, marginTop: '4px', lineHeight: 1.5, margin: '4px 0 0' }}>{test.message}</p>}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

            {allChallenges.length > 0 && (
              <div style={{ marginTop: 'auto', paddingTop: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '11px', color: c.muted }}>{currentIndex + 1} / {allChallenges.length}</span>
                  <span style={{ fontSize: '11px', color: c.muted }}>{challenge.topic}</span>
                </div>
                <div style={{ background: c.border, borderRadius: '2px', height: '2px', marginBottom: '10px' }}>
                  <div style={{ width: `${((currentIndex + 1) / allChallenges.length) * 100}%`, height: '2px', background: c.accent, borderRadius: '2px', transition: 'width 0.3s' }} />
                </div>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                  <button onClick={() => currentIndex > 0 && goToChallenge(currentIndex - 1)} disabled={currentIndex <= 0}
                    style={{ flex: 1, background: 'transparent', border: `1px solid ${c.border}`, color: currentIndex <= 0 ? c.muted : c.secondary, padding: '8px', borderRadius: '6px', cursor: currentIndex <= 0 ? 'not-allowed' : 'pointer', fontSize: '12px' }}>← Prev</button>
                  <button onClick={() => currentIndex < allChallenges.length - 1 && goToChallenge(currentIndex + 1)} disabled={currentIndex >= allChallenges.length - 1}
                    style={{ flex: 1, background: 'transparent', border: `1px solid ${c.border}`, color: currentIndex >= allChallenges.length - 1 ? c.muted : c.secondary, padding: '8px', borderRadius: '6px', cursor: currentIndex >= allChallenges.length - 1 ? 'not-allowed' : 'pointer', fontSize: '12px' }}>Next →</button>
                </div>
                <button onClick={() => navigate(backUrl(challenge.category))}
                  style={{ width: '100%', background: 'transparent', border: 'none', color: c.muted, padding: '6px', cursor: 'pointer', fontSize: '12px' }}>← Back to challenges</button>
              </div>
            )}
          </div>
        )}
      </div>

      <EinsteinMascot />
    </div>
  );
}